import { toast } from 'react-toastify';
import appApi from '../../../../../../api/app-api';
import { displayErrorText } from '../../../../../../api/errors/api-error-codes';
import { AccountingDepositDetailsEntity } from '../../../../../../api/types/accounting/accounting-deposit-details-entity';
import { AccountingDepositPaymentDetailsEntity } from '../../../../../../api/types/accounting/accounting-deposit-payment-details-entity';
import { AccountingDepositPaymentEntity } from '../../../../../../api/types/accounting/accounting-deposit-payment-entity';
import { DepositPaymentType } from '../../../../../../api/types/accounting/deposit-payment-type';
import generateAndDownloadExcelFile from '../../../../../../component/excel/generate-and-download-excel-file';
import { formatDate } from '../../../../../../utils/date-format';

// large enough to cover any realistic deposit; backend paginates server-side
const EXPORT_LIMIT = 100000;

const paymentTypeLabel = (type: DepositPaymentType): string => {
  switch (type) {
    case DepositPaymentType.CASH:
      return 'מזומן';
    case DepositPaymentType.CHECK:
      return 'המחאה';
    default:
      return type;
  }
};

const paymentSourceLabel = (p: AccountingDepositPaymentEntity): string => {
  if (p.userPaymentId !== null) return 'תשלום סטודנט';
  if (p.userPaymentCheckDetailsId !== null) return 'המחאת סטודנט';
  if (p.crmPaymentId !== null) return 'תשלום CRM';
  if (p.crmCheckId !== null) return 'המחאה CRM';
  return '';
};

const bankAccountLabel = (
  deposit: AccountingDepositDetailsEntity | null,
): string => {
  if (!deposit) return '';
  const account = deposit.bankAccount;
  if (account) {
    const parts = [account.accountCode, account.accountName].filter(
      (v) => v && String(v).trim().length > 0,
    );
    if (parts.length > 0) return parts.join(' - ');
  }
  return deposit.bankAccountId !== null && deposit.bankAccountId !== undefined
    ? String(deposit.bankAccountId)
    : '';
};

export const exportDepositToExcelOperation = async (
  depositId: number,
  depositDetails: AccountingDepositDetailsEntity | null,
  setExporting: (exporting: boolean) => void,
) => {
  try {
    setExporting(true);

    const res = await appApi.getAccountingDepositById(
      depositId,
      0,
      EXPORT_LIMIT,
    );

    if (res.data.errCode) {
      toast.warn(displayErrorText(res.data.errCode));
      setExporting(false);
      return;
    }

    const loadedDeposit = res.data.body?.deposit ?? null;
    const payments: AccountingDepositPaymentDetailsEntity[] =
      res.data.body?.payments?.data ?? [];

    // Use the loaded (fresh) deposit if returned, fall back to the dialog's
    // details entity (which carries `bankAccount` lookup + counts).
    const depositForExport: AccountingDepositDetailsEntity | null =
      depositDetails ? { ...depositDetails, ...(loadedDeposit ?? {}) } : null;

    if (!depositForExport && !loadedDeposit) {
      toast.warn('לא נמצאו פרטי הפקדה לייצוא');
      setExporting(false);
      return;
    }

    const depositId_ = depositForExport?.id ?? loadedDeposit?.id ?? depositId;
    const depositDate =
      depositForExport?.depositDate ?? loadedDeposit?.depositDate;
    const depositDateStr = depositDate ? formatDate(depositDate as Date) : '';
    const depositCreatedAt =
      depositForExport?.createdAt ?? loadedDeposit?.createdAt;
    const depositCreatedAtStr = depositCreatedAt
      ? formatDate(depositCreatedAt as Date)
      : '';
    const totalAmount =
      depositForExport?.totalAmount ?? loadedDeposit?.totalAmount;
    const totalAmountStr =
      typeof totalAmount === 'number' ? totalAmount.toFixed(2) : '';
    const bankAccountStr = bankAccountLabel(depositForExport);
    const createdByName = (
      depositForExport?.createdByName ??
      loadedDeposit?.createdByName ??
      ''
    ).trim();
    const notes =
      (depositForExport?.notes ?? loadedDeposit?.notes ?? '')?.trim() ?? '';

    const headers = [
      'מזהה הפקדה',
      'תאריך הפקדה',
      'סכום כולל ההפקדה',
      'חשבון בנק',
      'נוצר ע״י',
      'תאריך יצירת הפקדה',
      'הערות',
      'מזהה תשלום',
      'סוג תשלום',
      'מקור',
      // Per-row monetary amount. Cash → `cashAmount`, check → `checkDetails.amount`.
      // Mirrors the "סכום" column added to the deposit-details dialog.
      'סכום',
      // Check-only columns. Cash rows leave these blank. Mirrors the
      // בנק/סניף/חשבון/אסמכתא columns in the deposit-details dialog.
      'בנק',
      'סניף',
      'חשבון',
      'אסמכתא',
      // Per-row deposit date (mirrors the "תאריך הפקדה" column in the deposit
      // details dialog). Distinct header from the deposit-level "תאריך הפקדה"
      // above to avoid duplicate-header confusion in Excel.
      'תאריך הפקדה (לפי תשלום)',
    ];

    const depositContextCells = [
      depositId_,
      depositDateStr,
      totalAmountStr,
      bankAccountStr,
      createdByName,
      depositCreatedAtStr,
      notes,
    ];

    // Per-row "deposit date":
    //   - check rows → the check's clearing date (`checkDetails.dueDate`)
    //   - cash rows  → the deposit-level date (already formatted above)
    // Mirrors the "תאריך הפקדה" column added to the deposit-details dialog.
    const perRowDepositDate = (
      p: AccountingDepositPaymentDetailsEntity,
    ): string => {
      if (p.paymentType === DepositPaymentType.CHECK) {
        return p.checkDetails?.dueDate
          ? formatDate(p.checkDetails.dueDate as Date)
          : '';
      }
      return depositDateStr;
    };

    // Per-row amount: cash rows → `cashAmount`, check rows → `checkDetails.amount`.
    // Mirrors the "סכום" column added to the deposit-details dialog.
    const perRowAmount = (p: AccountingDepositPaymentDetailsEntity): string => {
      if (p.paymentType === DepositPaymentType.CASH) {
        return typeof p.cashAmount === 'number' ? p.cashAmount.toFixed(2) : '';
      }
      if (p.paymentType === DepositPaymentType.CHECK) {
        return typeof p.checkDetails?.amount === 'number'
          ? p.checkDetails.amount.toFixed(2)
          : '';
      }
      return '';
    };

    // Cash rows have `checkDetails: null`, so the check columns stay blank.
    // For checks we trim defensively because `referenceId` is nullable on the
    // entity (other fields are string but may also arrive empty in practice).
    const checkDetailCell = (
      p: AccountingDepositPaymentDetailsEntity,
      read: (
        d: NonNullable<AccountingDepositPaymentDetailsEntity['checkDetails']>,
      ) => string | null | undefined,
    ): string => {
      if (!p.checkDetails) return '';
      const value = read(p.checkDetails);
      return value ? String(value).trim() : '';
    };

    const dataRows: (string | number)[][] = payments.length
      ? payments.map((p) => [
          ...depositContextCells,
          p.id,
          paymentTypeLabel(p.paymentType),
          paymentSourceLabel(p),
          perRowAmount(p),
          checkDetailCell(p, (d) => d.bankNumber),
          checkDetailCell(p, (d) => d.branchNumber),
          checkDetailCell(p, (d) => d.accountNumber),
          checkDetailCell(p, (d) => d.referenceId),
          perRowDepositDate(p),
        ])
      : [[...depositContextCells, '', '', '', '', '', '', '', '', '']];

    const safeDateForFileName = depositDateStr.replace(/\//g, '-');
    const fileName = `הפקדה ${depositId_}${safeDateForFileName ? ` - ${safeDateForFileName}` : ''}`;
    const sheetName = `הפקדה #${depositId_}`;

    generateAndDownloadExcelFile([headers, ...dataRows], {
      fileName,
      sheetName,
    });

    setExporting(false);
  } catch (e) {
    toast.error('משהו קרה, יש לנסות שוב בעוד מספר רגעים');
    setExporting(false);
  }
};
